import { motion } from "framer-motion";
import { Rocket, BookOpen, Users, TrendingUp } from "lucide-react";

const REASONS = [
  {
    n: "01",
    Icon: Rocket,
    title: "Challenging Projects",
    desc: "Real-world web applications, CRM systems, APIs, automation platforms and business solutions used by teams across the US, EU and APAC.",
  },
  {
    n: "02",
    Icon: BookOpen,
    title: "Continuous Learning",
    desc: "Work with modern technologies and expand your technical skills through pairing, code reviews and AI-augmented workflows.",
  },
  {
    n: "03",
    Icon: Users,
    title: "Collaborative Team",
    desc: "Work alongside senior developers, designers, QA and technology specialists who ship — and hold each other to a high bar.",
  },
  {
    n: "04",
    Icon: TrendingUp,
    title: "Growth Opportunities",
    desc: "Build deep expertise and take on greater technical, architectural and project leadership responsibilities over time.",
  },
];

export default function WhyJoin() {
  return (
    <section
      id="why-join"
      data-testid="why-join-section"
      className="relative border-t border-hairline py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20 items-end">
          <div className="md:col-span-3">
            <span className="mono-label">[01] Why Beacon</span>
          </div>
          <div className="md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 0.7 }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.98] max-w-[20ch]"
            >
              Great technology is built by{" "}
              <span className="text-[color:var(--accent)]">great people</span>.
            </motion.h2>
            <p className="mt-6 md:mt-8 max-w-[62ch] text-white/70 text-[15px] md:text-[16px] leading-[1.6]">
              At Beacon IT Solutions, we provide an environment where talented professionals can work on
              challenging projects, learn modern technologies, solve real business problems and
              grow their careers.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {REASONS.map((r, i) => (
            <motion.div
              key={r.n}
              data-testid={`why-join-card-${r.n}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.55, delay: i * 0.05, ease: [0.2, 0.7, 0.2, 1] }}
              className="group relative border border-hairline bg-surface hover:bg-surface-2 transition-colors p-6 md:p-8 flex flex-col justify-between min-h-[240px]"
            >
              <div className="flex items-start justify-between">
                <span className="mono-label !text-white/70">[{r.n}]</span>
                <r.Icon strokeWidth={1.25} className="text-[color:var(--accent)]" size={26} />
              </div>
              <div className="mt-10">
                <h3 className="font-display text-[20px] md:text-[22px] leading-[1.1] tracking-tight">
                  {r.title}
                </h3>
                <p className="mt-3 text-white/65 text-[13px] md:text-[14px] leading-[1.55]">
                  {r.desc}
                </p>
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[color:var(--accent)]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
